import { useLayoutEffect, type RefObject } from 'react'
import {
  animate,
  createDrawable,
  createScope,
  createTimeline,
  onScroll,
  stagger,
} from 'animejs'

export function useNovaMotion(
  root: RefObject<HTMLDivElement | null>,
  motionEnabled: boolean,
) {
  useLayoutEffect(() => {
    const rootElement = root.current
    if (!rootElement || !motionEnabled) return

    const scope = createScope({ root: rootElement }).add(() => {
      createTimeline({ defaults: { ease: 'outExpo' } })
        .add(
          '.site-header',
          {
            opacity: [0, 1],
            y: ['-1.25rem', '0rem'],
            duration: 760,
          },
          0,
        )
        .add(
          '.hero__signal',
          {
            opacity: [0, 1],
            y: ['1rem', '0rem'],
            duration: 680,
          },
          120,
        )
        .add(
          '.hero__line-inner',
          {
            opacity: [0, 1],
            y: ['118%', '0%'],
            delay: stagger(92),
            duration: 1_180,
          },
          210,
        )
        .add(
          '.hero__meta',
          {
            opacity: [0, 1],
            y: ['1.5rem', '0rem'],
            duration: 760,
          },
          '<<+=520',
        )
        .add(
          '.hero__stage-shell',
          {
            opacity: [0, 1],
            duration: 1_280,
          },
          '<<+=120',
        )
        .add(
          '.hero__coordinates, .hero__scroll',
          {
            opacity: [0, 1],
            duration: 620,
          },
          '<<+=480',
        )

      animate('.capture-line__beam', {
        scaleY: [0, 1],
        opacity: [0, 1],
        duration: 1_900,
        ease: 'inOutQuart',
      })

      animate(createDrawable('.hero-orbit__path'), {
        draw: ['0 0', '0 1'],
        duration: 1_800,
        delay: stagger(130),
        ease: 'inOutQuad',
      })

      rootElement
        .querySelectorAll<HTMLElement>('[data-reveal]')
        .forEach((element) => {
          animate(element, {
            opacity: [0, 1],
            y: ['3rem', '0rem'],
            duration: 900,
            ease: 'outExpo',
            autoplay: onScroll({
              target: element,
              enter: 'bottom top',
              leave: 'top bottom',
              sync: 'play pause',
            }),
          })
        })

      animate('.origin__statement-line:nth-child(odd)', {
        x: ['-2.5vw', '0vw'],
        autoplay: onScroll({
          target: '#origin',
          enter: 'bottom top',
          leave: 'top bottom',
          sync: 0.35,
        }),
        ease: 'linear',
      })

      animate('.origin__statement-line:nth-child(even)', {
        x: ['2.5vw', '0vw'],
        autoplay: onScroll({
          target: '#origin',
          enter: 'bottom top',
          leave: 'top bottom',
          sync: 0.35,
        }),
        ease: 'linear',
      })

    })

    return () => scope.revert()
  }, [motionEnabled, root])
}
